import { Button } from '@mui/material';
import { endOfDay, format, isAfter, isBefore, startOfDay } from 'date-fns';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchAnnualSolarReportsForSiteWithRange,
  getAnnualSolarReportsForSelectedStructure,
} from '@sophocles/entities/AnnualSolarReport';
import {
  fetchDailySolarReportsForSiteWithRange,
  getDailySolarReportsForSelectedStructure,
} from '@sophocles/entities/DailySolarReport';
import {
  fetchMonthlySolarReportsForSiteWithRange,
  getMonthlySolarReportsForSelectedStructure,
} from '@sophocles/entities/MonthlySolarReport';
import {
  fetchWeeklySolarReportsForSiteWithRange,
  getWeeklySolarReportsForSelectedStructure,
} from '@sophocles/entities/WeeklySolarReport';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { formatDateForSubmission } from '@sophocles/utils/formatUtils';
import AnsiDoubleConsumptionChart from '@sophocles/views/sites/components/AnsiDoubleConsumptionChart';
import AnsiDoubleConsumptionTable from '@sophocles/views/sites/components/AnsiDoubleConsumptionTable';

const ExternalPowerReadings = ({ site, startDate, endDate }) => {
  const { t } = useTranslation();
  const siteTerm = useSiteTerm();
  const dispatch = useDispatch();

  const dailySolarReports = useSelector(getDailySolarReportsForSelectedStructure);
  const weeklySolarReports = useSelector(getWeeklySolarReportsForSelectedStructure);
  const monthlySolarReports = useSelector(getMonthlySolarReportsForSelectedStructure);
  const yearlySolarReports = useSelector(getAnnualSolarReportsForSelectedStructure);

  const siteId = site?.get('id', null);

  const [tab, setTab] = useState('daily');
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    if (!siteId) return;

    setLoading(true);
    const formattedStart = formatDateForSubmission(startDate);
    const formattedEnd = formatDateForSubmission(endDate);

    switch (tab) {
      case 'daily':
        dispatch(fetchDailySolarReportsForSiteWithRange(siteId, formattedStart, formattedEnd));
        break;
      case 'weekly':
        dispatch(fetchWeeklySolarReportsForSiteWithRange(siteId, formattedStart, formattedEnd));
        break;
      case 'monthly':
        dispatch(fetchMonthlySolarReportsForSiteWithRange(siteId, formattedStart, formattedEnd));
        break;
      case 'yearly':
        dispatch(fetchAnnualSolarReportsForSiteWithRange(siteId, formattedStart, formattedEnd));
        break;
      default:
        break;
    }

    setLoading(false);
  }, [dispatch, siteId, tab, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startDateTime = startOfDay(startDate);
  const endDateTime = endOfDay(endDate);

  let dataSource = [];
  let chartTitle = '';

  switch (tab) {
    case 'daily':
      chartTitle = t('powerData.dailyExternalMeterReadings', 'Daily External Meter Readings');
      dataSource = dailySolarReports;
      break;
    case 'weekly':
      chartTitle = t('powerData.weeklyExternalMeterReadings', 'Weekly External Meter Readings');
      dataSource = weeklySolarReports;
      break;
    case 'monthly':
      chartTitle = t('powerData.monthlyExternalMeterReadings', 'Monthly External Meter Readings');
      dataSource = monthlySolarReports;
      break;
    case 'yearly':
      chartTitle = t('powerData.yearlyExternalMeterReadings', 'Yearly External Meter Readings');
      dataSource = yearlySolarReports;
      break;
    default:
      break;
  }

  const dataList = dataSource
    .filter(data => {
      const m = data.get('moment');
      return isAfter(m, startDateTime) && isBefore(m, endDateTime);
    })
    .map(data => {
      const generated = data.get('generated', 0);
      const consumed = data.get('consumed', 0);
      return data.merge({
        generated: _.round(generated, 3),
        consumed: _.round(consumed, 3),
        short: format(data.get('moment', new Date()), 'MM/dd'),
      });
    })
    .sortBy(item => item.get('moment'))
    .reverse();

  const plainList = dataList.toJS();
  _.reverse(plainList);

  return siteId !== 0 ? (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 8 }}>
          <Button
            variant="contained"
            style={{ margin: '0 3px' }}
            color={tab === 'daily' ? 'primary' : 'secondary'}
            onClick={() => setTab('daily')}
          >
            {t('general.daily', 'Daily')}
          </Button>
          <Button
            variant="contained"
            style={{ margin: '0 3px' }}
            color={tab === 'weekly' ? 'primary' : 'secondary'}
            onClick={() => setTab('weekly')}
          >
            {t('general.weekly', 'Weekly')}
          </Button>
          <Button
            variant="contained"
            style={{ margin: '0 3px' }}
            color={tab === 'monthly' ? 'primary' : 'secondary'}
            onClick={() => setTab('monthly')}
          >
            {t('general.monthly', 'Monthly')}
          </Button>
          <Button
            variant="contained"
            style={{ margin: '0 3px' }}
            color={tab === 'yearly' ? 'primary' : 'secondary'}
            onClick={() => setTab('yearly')}
          >
            {t('general.yearly', 'Yearly')}
          </Button>
        </div>
      </div>
      <div>
        <h4 style={{ textAlign: 'center' }}>{chartTitle}</h4>
        <AnsiDoubleConsumptionChart list={plainList} />
        <div style={{ height: '400px' }}>
          <AnsiDoubleConsumptionTable unit_id={siteId} list={dataList} />
        </div>
      </div>
    </>
  ) : (
    <h2 style={{ textAlign: 'center' }}>
      {t('powerData.noReadingsOnSite', 'No readings on this {{site}}.', { site: siteTerm.singular.toLowerCase() })}
    </h2>
  );
};

export default ExternalPowerReadings;
